import React, { useEffect, useState, useCallback } from "react";
import { api, fileUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftRight, Check, X, Loader2, Gift, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABELS = {
  awaiting_owner_selection: "Waiting on the owner to pick items",
  awaiting_initiator_agree: "Waiting on the requester to agree",
  awaiting_owner_accept: "Waiting on the owner to accept",
  awaiting_fees: "Waiting on the $2 barter fees",
  completed: "Trade sealed",
  declined: "Trade closed",
};

function TosModal({ open, onClose, onAgree, busy }) {
  const [tos, setTos] = useState([]);
  const [checked, setChecked] = useState(false);
  useEffect(() => { if (open) { setChecked(false); api.get("/trades/tos").then((r) => setTos(r.data.tos)).catch(() => {}); } }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="trade-tos-modal">
      <div className="bg-white max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-3">
          <Gift size={18} style={{ color: "#2563EB" }} />
          <h3 className="font-display text-xl">Barter terms of service</h3>
        </div>
        <p className="text-xs text-neutral-500 mb-4">
          By clicking the agree button below, you are stating that you have read and agreed to the terms below.
        </p>
        <ol className="space-y-3 text-sm" style={{ color: "#3A423A" }}>
          {tos.map((t, i) => (
            <li key={i} className="flex gap-2"><span className="font-semibold">{i + 1}:</span><span>{t}</span></li>
          ))}
        </ol>
        <label className="mt-5 flex items-start gap-2 text-sm cursor-pointer" data-testid="trade-tos-check">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-1" />
          <span>I have read and agree to the barter terms above, including the non-refundable $2 barter fee.</span>
        </label>
        <div className="mt-5 flex gap-2">
          <button className="btn-primary" disabled={!checked || busy || tos.length === 0} onClick={onAgree} data-testid="trade-tos-agree">
            {busy ? "…" : "Agree"}
          </button>
          <button className="chip" onClick={onClose} data-testid="trade-tos-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ItemChip({ it }) {
  if (!it) return null;
  return (
    <div className="flex items-center gap-2 hairline p-2">
      <img src={fileUrl(it.image_path)} alt="" className="w-10 h-10 object-cover" />
      <div className="min-w-0">
        <div className="text-xs truncate">{it.title}</div>
        <div className="text-xs text-neutral-500">${it.price}</div>
      </div>
    </div>
  );
}

function SelectPanel({ trade, onDone }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/trades/${trade.id}/offerable`).then((r) => { setData(r.data); setPicked(r.data.selected || []); }).catch(() => {});
  }, [trade.id]);
  const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const submit = async () => {
    if (!picked.length) { toast.error("Pick at least one item you'd trade for"); return; }
    setBusy(true);
    try {
      await api.post(`/trades/${trade.id}/select`, { offered_listing_ids: picked });
      toast.success("Sent to the requester"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  if (!data) return <p className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading their items…</p>;
  return (
    <div data-testid="trade-select-panel">
      <p className="text-sm text-neutral-600 mb-3">Browse <span className="font-medium">{trade.initiator_name}</span>'s items — tap any you'd trade your item for, then hit Done.</p>
      {data.listings.length === 0 ? (
        <p className="text-sm text-neutral-500">This member has no items available to trade right now.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {data.listings.map((it) => {
            const on = picked.includes(it.id);
            return (
              <button key={it.id} type="button" onClick={() => toggle(it.id)}
                data-testid={`trade-pick-${it.id}`}
                className="text-left hairline p-2 transition-all"
                style={on ? { borderColor: "#16A34A", boxShadow: "0 0 0 2px #16A34A", background: "#F0FDF4" } : {}}>
                <div className="relative aspect-square">
                  <img src={fileUrl(it.image_path)} alt="" className="w-full h-full object-cover" />
                  {on && <span className="absolute top-1 right-1 bg-green-600 text-white rounded-full p-0.5"><Check size={12} /></span>}
                </div>
                <div className="mt-1 text-xs truncate">{it.title}</div>
                <div className="text-xs text-neutral-500">${it.price}</div>
              </button>
            );
          })}
        </div>
      )}
      <button className="btn-primary mt-4" disabled={busy || !picked.length} onClick={submit} data-testid="trade-done-btn">
        {busy ? "Sending…" : `Done — offer ${picked.length} item${picked.length === 1 ? "" : "s"}`}
      </button>
    </div>
  );
}

function TradeCard({ trade, me, refresh }) {
  const [tos, setTos] = useState(null); // "agree" | "accept"
  const [busy, setBusy] = useState(false);
  const isOwner = me === trade.owner_id;
  const isInitiator = me === trade.initiator_id;
  const s = trade.status;

  const act = async (fn) => { setBusy(true); try { await fn(); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const disagree = () => act(() => api.post(`/trades/${trade.id}/disagree`));
  const deny = () => act(() => api.post(`/trades/${trade.id}/deny`));
  const agree = () => act(async () => { await api.post(`/trades/${trade.id}/agree`, { agree_tos: true }); setTos(null); toast.success("Sent — waiting on the owner to accept"); });
  const accept = () => act(async () => { await api.post(`/trades/${trade.id}/accept`, { agree_tos: true }); setTos(null); toast.success("Accepted — now both pay the $2 barter fee"); });
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trades/${trade.id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setBusy(false); }
  };

  const myFeePaid = isOwner ? trade.owner_fee_paid : trade.initiator_fee_paid;

  return (
    <div className="hairline p-5" data-testid={`trade-card-${trade.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ArrowLeftRight size={15} style={{ color: "#2563EB" }} />
          <span className="label-eyebrow">{isOwner ? `Trade request from ${trade.initiator_name}` : `Your trade with ${trade.owner_name}`}</span>
        </div>
        <span className="chip" data-testid={`trade-status-${trade.id}`}>{STATUS_LABELS[s] || s}</span>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-neutral-500 mb-1">{isOwner ? "Your item they want" : "Item you want"}</div>
          <ItemChip it={trade.target} />
        </div>
        {trade.offered.length > 0 && (
          <div>
            <div className="text-xs text-neutral-500 mb-1">{isOwner ? "You'll trade for" : "They'll trade for"}</div>
            <div className="space-y-2">{trade.offered.map((it) => <ItemChip key={it.id} it={it} />)}</div>
          </div>
        )}
      </div>

      {/* Owner selects items */}
      {s === "awaiting_owner_selection" && isOwner && (
        <div className="mt-5 pt-4 border-t border-black/10"><SelectPanel trade={trade} onDone={refresh} /></div>
      )}
      {s === "awaiting_owner_selection" && isInitiator && (
        <p className="mt-4 text-sm text-neutral-500">Waiting for {trade.owner_name} to browse your items and choose what to trade for.</p>
      )}

      {/* Initiator agrees / disagrees */}
      {s === "awaiting_initiator_agree" && isInitiator && (
        <div className="mt-5 pt-4 border-t border-black/10 flex gap-2">
          <button className="btn-primary" onClick={() => setTos("agree")} disabled={busy} data-testid={`trade-agree-${trade.id}`}>Agree to this trade</button>
          <button className="chip" onClick={disagree} disabled={busy} data-testid={`trade-disagree-${trade.id}`}><X size={12} /> Disagree</button>
        </div>
      )}
      {s === "awaiting_initiator_agree" && isOwner && (
        <p className="mt-4 text-sm text-neutral-500">Waiting for {trade.initiator_name} to agree to the trade.</p>
      )}

      {/* Owner accepts / denies */}
      {s === "awaiting_owner_accept" && isOwner && (
        <div className="mt-5 pt-4 border-t border-black/10 flex gap-2">
          <button className="btn-primary" onClick={() => setTos("accept")} disabled={busy} data-testid={`trade-accept-${trade.id}`}>Accept the trade</button>
          <button className="chip" onClick={deny} disabled={busy} data-testid={`trade-deny-${trade.id}`}><X size={12} /> Deny</button>
        </div>
      )}
      {s === "awaiting_owner_accept" && isInitiator && (
        <p className="mt-4 text-sm text-neutral-500">You agreed. Waiting for {trade.owner_name} to accept.</p>
      )}

      {/* Both pay */}
      {s === "awaiting_fees" && (
        <div className="mt-5 pt-4 border-t border-black/10">
          <p className="text-sm text-neutral-600 mb-3">Both sides pay a one-time <span className="font-medium">$2 barter fee</span> to seal the trade. You: {myFeePaid ? <span className="text-green-600 font-medium">paid ✓</span> : <span className="text-red-600">not paid</span>} · Them: {(isOwner ? trade.initiator_fee_paid : trade.owner_fee_paid) ? <span className="text-green-600 font-medium">paid ✓</span> : "not paid"}</p>
          {!myFeePaid
            ? <button className="btn-primary" onClick={pay} disabled={busy} data-testid={`trade-pay-${trade.id}`}>{busy ? "…" : "Pay $2 barter fee"}</button>
            : <p className="text-sm text-neutral-500">Waiting for the other side to pay their $2.</p>}
        </div>
      )}

      {/* Completed — addresses exchanged */}
      {s === "completed" && (
        <div className="mt-5 pt-4 border-t border-black/10" data-testid={`trade-completed-${trade.id}`}>
          <p className="text-sm text-green-700 font-medium mb-3">Trade sealed! Ship your item to the address below. Your Classic Style is not involved in the exchange.</p>
          <div className="hairline p-3 text-sm">
            <div className="flex items-center gap-1 text-neutral-500 text-xs mb-1"><MapPin size={12} /> Ship your item to {isOwner ? trade.initiator_name : trade.owner_name}</div>
            <Address a={isOwner ? trade.initiator_address : trade.owner_address} />
          </div>
        </div>
      )}

      {s === "declined" && (
        <p className="mt-4 text-sm text-neutral-500" data-testid={`trade-declined-${trade.id}`}>
          This trade was {trade.declined_by === "owner" ? "declined by the owner" : "withdrawn by the requester"}.
        </p>
      )}

      <TosModal open={tos === "agree"} onClose={() => setTos(null)} onAgree={agree} busy={busy} />
      <TosModal open={tos === "accept"} onClose={() => setTos(null)} onAgree={accept} busy={busy} />
    </div>
  );
}

function Address({ a }) {
  if (!a || !a.street1) return <span className="text-neutral-400">Address not set — message them for shipping details.</span>;
  return (
    <div className="text-neutral-700">
      {a.name && <div>{a.name}</div>}
      <div>{a.street1}{a.street2 ? `, ${a.street2}` : ""}</div>
      <div>{a.city}, {a.state} {a.zip}</div>
      <div>{a.country || "US"}</div>
    </div>
  );
}

export default function Trades() {
  const { user } = useAuth();
  const [trades, setTrades] = useState(null);
  const load = useCallback(() => { api.get("/trades").then((r) => setTrades(r.data)).catch(() => setTrades([])); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-3xl mx-auto px-5 py-12" data-testid="trades-page">
      <div className="flex items-center gap-2 mb-1"><ArrowLeftRight size={20} style={{ color: "#2563EB" }} /><span className="label-eyebrow">Barter</span></div>
      <h1 className="font-display text-3xl sm:text-4xl mb-6">My trades</h1>
      {!trades ? (
        <p className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : trades.length === 0 ? (
        <div className="hairline p-8 text-center" data-testid="trades-empty">
          <p className="text-sm text-neutral-500">No trades yet. Find an item with a green <span className="font-medium">Trade</span> badge and propose a swap.</p>
          <Link to="/browse" className="btn-outline mt-4 inline-flex">Browse items</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {trades.map((t) => <TradeCard key={t.id} trade={t} me={user?.id} refresh={load} />)}
        </div>
      )}
    </div>
  );
}
